import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Container, PageHeader, ContactButtons, INKS } from "@/components/brand";

const timeline = [
  { when: "Early 1990s", what: "Jagdish Bajaj opens a photocopying shop in Koramangala, running Xerox and Canon machines." },
  { when: "2002", what: "The business takes the name Print Xpress, with its flagship unit on 1st Main Road, 7th Block." },
  { when: "Since then", what: "Digital, offset and large-format flex presses are added, so almost every job is printed in-house." },
  { when: "Today", what: "Three branches — Koramangala 7th Block, Koramangala 5th Block and Richmond Road — serving homes and businesses across the city." },
];

const audiences = [
  { title: "Businesses", body: "Corporate branding, brochures, visiting cards and multi-colour offset printing." },
  { title: "Events", body: "Traditional and modern wedding invitations, including recycled-paper options, plus banners and standees." },
  { title: "Personal projects", body: "Custom 3D wallpapers, posters, banners and personalised ID cards." },
];

const principles = [
  { title: "Precision", body: "Well-maintained machines and high-grade inks on every job, small or large." },
  { title: "Your spec first", body: "We follow your brief to the letter and check with you before we change anything." },
  { title: "Less waste", body: "Recycled-paper cards and other eco-friendly stock whenever you want them." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="min-h-screen bg-paper">
      <Navbar />
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "About" }]}
        title="From a photocopy counter to three print shops"
        lede="Print Xpress is a unit of Gurubal Engineering Co. Pvt. Ltd. We've been printing for Bengaluru from the same Koramangala street for more than two decades."
      />

      <main>
        <section className="py-16 md:py-24" aria-labelledby="story">
          <Container className="grid gap-12 lg:grid-cols-[1fr_1.5fr]">
            <h2 id="story" data-testid="about-hero-heading" className="font-display font-bold text-3xl sm:text-5xl text-ink">How we got here</h2>
            <ol className="relative border-l-2 border-ink ml-2">
              {timeline.map((t, i) => (
                <li key={t.when} className="relative pl-8 sm:pl-10 pb-10 last:pb-0">
                  <span className="absolute -left-[9px] top-1.5 w-4 h-4 border-2 border-ink" style={{ backgroundColor: INKS[i] }} aria-hidden="true" />
                  <p className="font-display font-bold text-2xl sm:text-3xl text-ink">{t.when}</p>
                  <p className="mt-2 text-lg text-graphite leading-relaxed max-w-[60ch]">{t.what}</p>
                </li>
              ))}
            </ol>
          </Container>
        </section>

        <section className="bg-process-cyan border-y border-ink py-16 md:py-24" aria-labelledby="who">
          <Container>
            <h2 id="who" className="font-display font-bold text-3xl sm:text-5xl text-ink max-w-2xl">Who we print for</h2>
            <ul className="mt-10 grid gap-px bg-ink border border-ink md:grid-cols-3">
              {audiences.map((a) => (
                <li key={a.title} className="bg-stock p-6 sm:p-8" data-testid={`serve-card-${a.title}`}>
                  <h3 className="font-display font-bold text-2xl text-ink">{a.title}</h3>
                  <p className="mt-3 text-graphite leading-relaxed">{a.body}</p>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        <section className="py-16 md:py-24" aria-labelledby="how">
          <Container className="grid gap-12 lg:grid-cols-[1fr_1.5fr]">
            <div>
              <h2 id="how" className="font-display font-bold text-3xl sm:text-5xl text-ink">How we work</h2>
              <p className="mt-5 text-lg text-graphite leading-relaxed max-w-md">
                Our goal is simple: digital and flex printing that's fast, good and fairly priced, with no surprises on the bill.
              </p>
            </div>
            <div>
              <ul className="grid gap-8 sm:grid-cols-3">
                {principles.map((p, i) => (
                  <li key={p.title} className="border-t-[3px] pt-5" style={{ borderColor: INKS[i] }}>
                    <h3 className="font-display font-bold text-xl text-ink">{p.title}</h3>
                    <p className="mt-2 text-graphite leading-relaxed">{p.body}</p>
                  </li>
                ))}
              </ul>
              <dl className="mt-12 grid grid-cols-2 border-t border-l border-ink">
                <div className="border-r border-b border-ink p-5 sm:p-8">
                  <dt className="sr-only">Print speed</dt>
                  <dd><span className="block font-display font-bold text-4xl sm:text-6xl tracking-[-0.04em] text-ink">300</span>
                    <span className="mt-2 block text-graphite">A4 sheets a minute at full speed</span></dd>
                </div>
                <div className="border-r border-b border-ink p-5 sm:p-8">
                  <dt className="sr-only">Team size</dt>
                  <dd><span className="block font-display font-bold text-4xl sm:text-6xl tracking-[-0.04em] text-ink">26–50</span>
                    <span className="mt-2 block text-graphite">people on the team</span></dd>
                </div>
              </dl>
            </div>
          </Container>
        </section>

        <section className="bg-ink text-paper py-16 md:py-20">
          <Container className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="max-w-xl">
              <h2 className="font-display font-bold text-3xl sm:text-5xl">Got something to print?</h2>
              <p className="mt-4 text-lg text-paper/75">Send us the details and we'll come back with a price, usually the same day.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact#quote" data-testid="about-cta" className="btn btn-paper">Get a quote</Link>
              <ContactButtons dark />
            </div>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
}
